import { Typography } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  EMIR_RESPONSE_MODEL_STANDARD,
  getEmirTableExportFileName,
  type EmirBreakdownPayload,
} from '../../../../../../api/rs-hg/emir';
import { clientNameSelector } from '../../../../../../redux/auth/selectors';
import CustomTable from '../../../../../tables/CustomTable';
import AssetLevelTableWrapper from './detailed/AssetLevelTableWrapper.component';
import {
  emirMancoLevelTableColumns,
  getEmirFundLevelTableColumns,
} from './emirFundSummary.columns';
import { useEmirFundSummaryStyles } from './emirFundSummary.styles';
import type {
  EmirFundLevelTableRow,
  EmirMancoLevelTableRow,
} from './emirFundSummary.types';
import {
  EMIR_TABLE_OPTIONS,
  buildEmirFundLevelTableData,
  buildEmirMancoLevelTableData,
  formatEmirSectionTitle,
} from './emirFundSummary.utils';

export type EmirFundSummaryVariant = 'standard' | 'detailed';

interface FundSummaryContainerProps {
  payload: EmirBreakdownPayload;
  selectedKey: string;
  variant: EmirFundSummaryVariant;
  exportPositionDate?: string;
}

function FundSummaryContainer({
  payload,
  selectedKey,
  variant,
  exportPositionDate,
}: FundSummaryContainerProps): ReactElement {
  const classes = useEmirFundSummaryStyles();
  const clientName = useSelector(clientNameSelector) || 'mersenne';
  const isStandard = variant === 'standard';
  const isDetailed = variant === 'detailed';

  const fundLevelTableData = useMemo(
    () =>
      buildEmirFundLevelTableData(payload.funds, selectedKey, {
        includeAssets: isDetailed,
      }),
    [payload.funds, selectedKey, isDetailed],
  );

  const fundLevelTableColumns = useMemo(
    () => getEmirFundLevelTableColumns(clientName),
    [clientName],
  );

  const mancoLevelTableData = useMemo(() => {
    if (
      !isStandard ||
      payload.emir_response_model !== EMIR_RESPONSE_MODEL_STANDARD
    ) {
      return [];
    }
    return buildEmirMancoLevelTableData(payload, selectedKey);
  }, [isStandard, payload, selectedKey]);

  const assetDetailPanel = useMemo(
    () => (isDetailed ? AssetLevelTableWrapper(exportPositionDate) : undefined),
    [isDetailed, exportPositionDate],
  );

  const mancoLevelExportFileName = exportPositionDate
    ? getEmirTableExportFileName('manco_level', exportPositionDate)
    : undefined;
  const fundLevelExportFileName = exportPositionDate
    ? getEmirTableExportFileName('fund_level', exportPositionDate)
    : undefined;

  return (
    <div className={classes.fundContainerRoot}>
      <div className={classes.title}>
        <Typography variant="h1">
          {formatEmirSectionTitle(selectedKey)}
        </Typography>
      </div>

      {isStandard ? (
        <div className={classes.mancoLevelTable}>
          <CustomTable<EmirMancoLevelTableRow>
            columns={emirMancoLevelTableColumns}
            showToolbar
            data={mancoLevelTableData}
            title="Manco Level Data"
            exportFileName={mancoLevelExportFileName}
            options={EMIR_TABLE_OPTIONS}
            pdfDontMoveToNewPage={true}
          />
        </div>
      ) : null}

      <div className={classes.fundLevelTable}>
        <CustomTable<EmirFundLevelTableRow>
          columns={fundLevelTableColumns}
          showToolbar
          data={fundLevelTableData}
          title="Fund Level Data"
          exportFileName={fundLevelExportFileName}
          options={EMIR_TABLE_OPTIONS}
          detailPanel={assetDetailPanel}
          pdfDontMoveToNewPage={true}
        />
      </div>
    </div>
  );
}

export default FundSummaryContainer;
